import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { XCircle, RefreshCcw, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConference } from '@/context/ConferenceContext';

const PaymentCancel = () => {
  const { importantDetails, siteConfig, getImportantDates } = useConference();
  return (
    <>
      <Helmet>
        <title>Payment Cancelled - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex w-24 h-24 rounded-full bg-red-100 items-center justify-center mb-6">
              <XCircle className="text-red-500" size={56} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Payment Cancelled
            </h1>
            <p className="text-xl text-gray-600">
              Your payment was not completed
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
              What Happened?
            </h2>
            <p className="text-gray-700 text-center mb-6">
              Your registration for {siteConfig?.fullName || importantDetails?.ConferenceTitle || 'our conference'} was not completed because the payment process was cancelled.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-6">
              <div className="flex">
                <HelpCircle className="text-yellow-400 mr-3 flex-shrink-0" size={24} />
                <div>
                  <p className="text-sm text-yellow-800 font-medium mb-2">
                    Don't worry - no charges were made to your account.
                  </p>
                  <p className="text-sm text-yellow-700">
                    Your registration information has been saved, and you can complete the payment process at any time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-4">Next Steps</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-semibold">1</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 mb-1">Return to Registration</p>
                  <p className="text-gray-600 text-sm">
                    Complete your registration and payment in one easy step
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-semibold">2</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 mb-1">Contact Support</p>
                  <p className="text-gray-600 text-sm">
                    If you experienced any issues, our team is here to help
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-semibold">3</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 mb-1">Try a Different Payment Method</p>
                  <p className="text-gray-600 text-sm">
                    We accept credit cards, debit cards, and bank transfers
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/register" className="flex-1 sm:flex-initial">
              <Button className="w-full bg-sky-500 hover:bg-sky-600">
                <RefreshCcw className="mr-2" size={18} />
                Try Again
              </Button>
            </Link>
            <Link to="/" className="flex-1 sm:flex-initial">
              <Button variant="outline" className="w-full">
                Return to Home
              </Button>
            </Link>
            <Link to="/contact" className="flex-1 sm:flex-initial">
              <Button variant="outline" className="w-full">
                Contact Support
              </Button>
            </Link>
          </div>

          <div className="bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl p-6 text-white text-center">
            <h3 className="font-semibold text-lg mb-2">Need Help?</h3>
            <p className="text-sky-50 mb-4">
              Our team is available to assist you with any questions or concerns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-sky-50">
              <a href={`mailto:${siteConfig?.contact?.email || 'info@conference.com'}`} className="hover:text-white font-medium">
                📧 {siteConfig?.contact?.email || 'info@conference.com'}
              </a>
              <a href={`tel:${siteConfig?.contact?.phone || '+1-234-567-8900'}`} className="hover:text-white font-medium">
                📞 {siteConfig?.contact?.phone || '+1-234-567-8900'}
              </a>
            </div>
          </div>

          <div className="mt-8 p-4 bg-sky-50 rounded-xl">
            <p className="text-sm text-gray-700 text-center">
              <strong>Reminder:</strong> Early bird registration rates are available until{' '}
              {getImportantDates().EarlyBird || siteConfig?.dates?.earlyBirdDeadline || 'the deadline'}.
              Register soon to save!
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentCancel;
